import { createFileRoute, Link } from '@tanstack/react-router';
import { useCheck, useSetCheckCleared, useVoidCheck } from '../../hooks/useChecks';
import { useCompanies } from '../../hooks/useCompanies';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { CheckFace } from '../../components/checks/CheckFace';
import { AttachmentsPanel } from '../../components/attachments/AttachmentsPanel';
import { ApiError } from '../../lib/api';
import { companyAddressLines, formatCurrency } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/checks/$id')({
  component: CheckDetailPage,
});

function CheckDetailPage() {
  const { id } = Route.useParams();
  const companyId = useActiveCompanyId();
  const { data: check, isLoading, error } = useCheck(companyId, id);
  const { data: companies = [] } = useCompanies();
  const clear = useSetCheckCleared(companyId);
  const voidCheck = useVoidCheck(companyId);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <p className="muted">No active company.</p>
      </div>
    );
  }
  if (isLoading) return <p className="muted">Loading…</p>;
  if (error || !check) return <p className="field-error">Failed to load check.</p>;

  const company = companies.find((c) => c.id === companyId);
  const companyName = company?.name ?? 'Company';
  const voidError =
    voidCheck.error instanceof ApiError
      ? (voidCheck.error.body as { error?: string } | null)?.error ?? 'Void failed'
      : voidCheck.error?.message;

  return (
    <div>
      <div className="no-print">
        <Link to="/checks" style={{ fontSize: 13 }}>← Check register</Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ fontSize: 24, margin: 0 }}>
            {check.checkNumber ? `Check #${check.checkNumber}` : 'Check (not yet printed)'}
            {check.status === 'cleared' && <span className="tag ok" style={{ marginLeft: 8 }}>cleared</span>}
            {check.status === 'voided' && <span className="tag bad" style={{ marginLeft: 8 }}>voided</span>}
            {check.status === 'outstanding' && <span className="tag warn" style={{ marginLeft: 8 }}>outstanding</span>}
            {check.status === 'to_print' && <span className="tag" style={{ marginLeft: 8 }}>to print</span>}
            {check.status === 'printing' && <span className="tag warn" style={{ marginLeft: 8 }}>printing</span>}
          </h1>
          <div style={{ display: 'flex', gap: 8 }}>
            {(check.status === 'outstanding' || check.status === 'to_print') && (
              <Link to="/checks/$id/edit" params={{ id: check.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
                Edit
              </Link>
            )}
            {check.status === 'to_print' && (
              <Link to="/checks/print-queue" className="btn-primary" style={{ textDecoration: 'none' }}>
                Go to print queue
              </Link>
            )}
            <Link to="/checks/new" search={{ copyFrom: check.id }} className="btn-secondary" style={{ textDecoration: 'none' }}>
              Copy
            </Link>
            {/* Browser print renders the on-screen facsimile — useful as a paper
                record, but it carries no MICR line. Real checks come off the
                batch printer via the print queue. */}
            {check.status !== 'to_print' && check.status !== 'printing' && (
              <button className="btn-secondary" onClick={() => window.print()}>Print copy</button>
            )}
            {check.status !== 'voided' && check.status !== 'printing' && (
              <>
                {check.status !== 'to_print' && (
                  <button
                    className="btn-secondary"
                    disabled={clear.isPending}
                    onClick={() => clear.mutate({ id: check.id, cleared: check.status !== 'cleared' })}
                  >
                    {check.status === 'cleared' ? 'Un-clear' : 'Mark cleared'}
                  </button>
                )}
                <button
                  className="btn-danger"
                  disabled={voidCheck.isPending}
                  onClick={() => {
                    const reason = prompt(
                      check.status === 'to_print'
                        ? 'Cancel this queued check? Reason:'
                        : 'Reason for voiding this check?',
                    );
                    if (reason && reason.trim()) voidCheck.mutate({ id: check.id, reason: reason.trim() });
                  }}
                >
                  {check.status === 'to_print' ? 'Cancel' : 'Void'}
                </button>
              </>
            )}
          </div>
        </div>
        {voidError && <div className="field-error" style={{ marginBottom: 12 }}>{voidError}</div>}
      </div>

      <CheckFace companyName={companyName} addressLines={companyAddressLines(company)} check={check} />

      <div className="card no-print" style={{ marginTop: 16, padding: 16, maxWidth: 760 }}>
        <h2 style={{ fontSize: 15, marginTop: 0 }}>Expense allocation</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Memo</th>
              <th style={{ textAlign: 'right' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {check.lines.map((l, i) => (
              <tr key={`${l.accountId}-${i}`}>
                <td>
                  {l.accountCode} — {l.accountName}
                  {l.settlesEntryNumber && (
                    <span className="muted" style={{ fontSize: 11, marginLeft: 6 }}>
                      pays {l.settlesEntryNumber}
                    </span>
                  )}
                </td>
                <td className="muted">{l.memo ?? ''}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
              <td colSpan={2}>Total</td>
              <td style={{ textAlign: 'right' }}>{formatCurrency(check.amount)}</td>
            </tr>
          </tfoot>
        </table>
        {check.journalEntryId && (
          <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
            Posted as{' '}
            <Link to="/journal/$id" params={{ id: check.journalEntryId }}>journal entry</Link>.
            {check.status === 'voided' && check.voidReason && ` Voided: ${check.voidReason}.`}
          </p>
        )}
      </div>

      <div className="no-print" style={{ marginTop: 16 }}>
        <AttachmentsPanel companyId={companyId} entityType="check" entityId={check.id} />
      </div>
    </div>
  );
}
